export {};
import { v4 as uuidv4 } from 'uuid';
import {
    ExcelParsedProperties as props,
    ExcelSkipParsingSheets,
    ExcelHeaderRowCount,
    ExcelAllowedHeaders,
    ExcelAlarmStringValues,
    ExcelChannelTypeMap,
    ExcelGroupCategory,
    GroupsSheetColumns,
    BlankChannelTypes,
    ExcelParsedProperties,
    normalizeLegacyExcelHeader,
} from '../models/channel-config';
import { AlarmLevel } from '../models/alarm-level';
import { ExcelImportError, ErrorCodes } from '../excel-import/error';
import { systemChannelType } from '../models/system-channels';
import { ChannelType, ChannelTypeStrings } from '../models/channel-type';
import { setChannelStatusFromString } from './channel-object-utils';
import { MotorType } from '../models/motor-alarm-setting';

export default class ChannelExcelParser {
    constructor() {}

    private static parseOptionalNumber(val: any): number | null {
        if (val === undefined || val === null || val === '') {
            return null;
        }
        const parsed = Number(val);
        return Number.isNaN(parsed) ? null : parsed;
    }

    static rowsToObjects(excelSpreadsheets: any) {
        const parsedData: any = [];

        excelSpreadsheets.forEach((spreadsheet: any) => {
            const { data, sheetName } = spreadsheet;

            if (sheetName == ExcelGroupCategory) {
                parsedData.push({ sheetName: sheetName, data: data });
            }

            if (!data || data.length == 0) {
                parsedData.push({ sheetName: sheetName, data: [] });
            } else if (!ExcelSkipParsingSheets.has(sheetName)) {
                const headers = this.parseHeaders(data);
                const headersUsed: any = {};
                for (const headerKey in props) {
                    headersUsed[props[headerKey]] = false;
                }

                for (let i = 0; i < headers.length; i++) {
                    if (!ExcelAllowedHeaders.has(headers[i].replace(/[\r\n]+/g, ' ').trim())) {
                        throw new ExcelImportError(ErrorCodes.UNKNOWN_HEADER, {
                            sheetName: sheetName,
                            header: headers[i],
                        });
                    } else {
                        headersUsed[headers[i].replace(/[\r\n]+/g, ' ').trim()] = true;
                    }
                }

                for (const header in headersUsed) {
                    if (
                        !headersUsed[header] &&
                        header !== ExcelParsedProperties.Scale &&
                        header !== ExcelParsedProperties.ChannelTypeSimple
                    ) {
                        throw new ExcelImportError(ErrorCodes.MISSING_HEADER, {
                            sheetName: sheetName,
                            header: header,
                        });
                    }
                }

                const rows = Array(Math.max(data.length - ExcelHeaderRowCount, 0));

                let groupRowIdsSet = new Set();
                const missingIdsRows: number[] = [];

                for (let i = ExcelHeaderRowCount; i < data.length; i++) {
                    if (!this.allValuesEmpty(data[i])) {
                        const excelRow = data[i];
                        const rowAsObject: any = {};

                        for (let j = 0; j < excelRow.length; j++) {
                            if (j == 0) {
                                const rowId = excelRow[j];
                                if (rowId && !groupRowIdsSet.has(rowId)) groupRowIdsSet.add(rowId);
                                else missingIdsRows.push(i - ExcelHeaderRowCount);
                            }
                            rowAsObject[headers[j].replace(/[\r\n]+/g, ' ').trim()] = excelRow[j];
                        }
                        rowAsObject.rowIndex = i - 4;

                        rows[i - ExcelHeaderRowCount] = rowAsObject;
                    }
                }

                for (let i = 0; i < missingIdsRows.length; i++) {
                    const rowNum = missingIdsRows[i];
                    const { randNum, currentRowIds } = this.generateExcelId(Number(sheetName), groupRowIdsSet);
                    if (rows[rowNum]) rows[rowNum].id = randNum.toString();
                    groupRowIdsSet = currentRowIds;
                }

                parsedData.push({ sheetName: sheetName, data: rows });
            }
        });

        let tagDuplicates = this.validIdIsUnique(parsedData, props.Tag);
        if (tagDuplicates) {
            throw new ExcelImportError(ErrorCodes.UNIQUENESS_VALUE, {
                column: tagDuplicates.column,
                row: tagDuplicates.row,
                sheetName: tagDuplicates.sheetName,
            });
        }

        let rowDuplicates = this.validIdIsUnique(parsedData, props.Id);
        if (rowDuplicates) {
            throw new ExcelImportError(ErrorCodes.UNIQUENESS_VALUE, {
                column: rowDuplicates.column,
                row: rowDuplicates.row,
                sheetName: rowDuplicates.sheetName,
            });
        }

        return parsedData;
    }

    static parseChannel(rowIndex: number, vesselId: string, groupId: string, rowId: string) {
        return {
            id: uuidv4(),
            vesselId,
            groupId,
            rowId,
            rowIndex,
        };
    }

    static generateExcelId(sheetNum, currentRowIds: Set<any>) {
        let combinedValue: number;
        do {
            let randomValue = Math.floor(8388607 * Math.random() + 1);
            let groupNum = sheetNum & 0xff;
            combinedValue = (randomValue * 0x100) | groupNum;
        } while (currentRowIds.has(combinedValue));

        return { randNum: combinedValue.toString(), currentRowIds };
    }

    static allValuesEmpty(arr: any[]) {
        for (let element of arr) {
            if (typeof element !== 'boolean' && element !== '') {
                return false;
            }
        }
        return true;
    }

    static parseOpsData(excelData: any, channelId: any, id?: string) {
        const opsData: any = {
            id: id ? id : uuidv4(),
            channelId: channelId,
        };

        if (excelData[props.ChannelType] === ChannelTypeStrings[ChannelType.BLANK]) {
            opsData.channelType = excelData[props.ChannelType];
            opsData.name = null;
            opsData.tag = null;
            opsData.measurementUnit = null;
            opsData.remarks = null;
            return opsData;
        }

        opsData.tag = excelData[props.Tag];
        opsData.channelType = excelData[props.ChannelType];
        opsData.measurementUnit = excelData[props.MeasurementUnit];
        opsData.name = excelData[props.Name];
        opsData.remarks = excelData[props.Remarks];

        return opsData;
    }

    static parseFcuData(excelData: any, channelId: any, id?: string) {
        if (ExcelChannelTypeMap[excelData[props.ChannelType]] == BlankChannelTypes) {
            const channelConfig = {
                id: id ? id : uuidv4(),
                channelId: channelId,
                channelType: ExcelChannelTypeMap[excelData[props.ChannelType]],
                fcuChannelId: null,
                fcuNo: null,
                measurableRange: null,
                normalRange: null,
                scale: null,
                fixedDigit: null,
                inputData: null,
                outputData: null,
                digitalAlarmSetting: null,
                motorChannelSetting: null,
                alarmSettingVeryHigh: null,
                alarmSettingHigh: null,
                alarmSettingLow: null,
                alarmSettingVeryLow: null,
                alarmSettingSensorFail: null,
                alarmSettingFeedbackError: null,
                systemAlarmSetting: null,
                standardNameId: null,
            };
            return channelConfig;
        }

        const checkEnable = (val: any): boolean => {
            if (val === undefined || val === null || val === '') return false;
            const strVal = String(val).trim().toUpperCase();
            return strVal !== 'FALSE' && strVal !== '0';
        };

        const isVHEnabled = checkEnable(excelData[props.AlarmSettingVeryHighEnable]);
        const isHEnabled = checkEnable(excelData[props.AlarmSettingHighEnable]);
        const isLEnabled = checkEnable(excelData[props.AlarmSettingLowEnable]);
        const isVLEnabled = checkEnable(excelData[props.AlarmSettingVeryLowEnable]);

        const values = [
            excelData[props.MeasurableRangeMax],
            excelData[props.MeasurableRangeMin],
            excelData[props.NormalRangeMax],
            excelData[props.NormalRangeMin],
        ];

        if (isVHEnabled) values.push(excelData[props.AlarmSettingVeryHighSetPoint]);
        if (isHEnabled) values.push(excelData[props.AlarmSettingHighSetPoint]);
        if (isLEnabled) values.push(excelData[props.AlarmSettingLowSetPoint]);
        if (isVLEnabled) values.push(excelData[props.AlarmSettingVeryLowSetPoint]);

        const scale = Math.max(...values.map(value => this.getScale(value)));
        const isValidValue = (val: any) => val !== undefined && val !== null && val !== '';

        const channelType = ExcelChannelTypeMap[excelData[props.ChannelType]];

        let channelConfig: any = {
            id: id ? id : uuidv4(),
            channelId: channelId,

            fcuChannelId: excelData[props.Tag],
            channelType: channelType,
            fcuNo: excelData[props.FcuNo] ? this.parseIntData(excelData[props.FcuNo]) : 1,

            measurableRange: this.toRange(
                excelData[props.MeasurableRangeMax],
                excelData[props.MeasurableRangeMin],
                scale
            ),
            normalRange: this.toRange(excelData[props.NormalRangeMax], excelData[props.NormalRangeMin], scale),
            fixedDigit: this.parseOptionalNumber(excelData[props.FixedDigit]),
            scale: scale,
            inputData: this.parseInputData(excelData),
            outputData: this.parseOutputData(excelData),
            digitalAlarmSetting: this.toAlarmSetting(
                excelData[props.DigitalAlarmSettingEnable],
                excelData[props.DigitalAlarmSettingStatusStringContactOn],
                excelData[props.DigitalAlarmSettingStatusStringContactOff],
                excelData[props.DigitalAlarmSettingAlarmGroupNo],
                excelData[props.DigitalAlarmSettingReposeGroup1No],
                excelData[props.DigitalAlarmSettingReposeGroup2No],
                excelData[props.DigitalAlarmSettingDelayTimer],
                excelData[props.DigitalAlarmSettingNormalOpenFlag],
                excelData[props.DigitalAlarmSettingAlarmLevel],
                false
            ),
            motorChannelSetting: this.toAlarmSettingMotor(
                channelType === ChannelType.DIGITAL_MOTOR,
                excelData[props.MotorChannelSettingMotorType],
                excelData[props.MotorChannelSettingStopStatusString],
                excelData[props.MotorChannelSettingStbyStatusString],
                excelData[props.MotorChannelSettingRunStatusString1],
                excelData[props.MotorChannelSettingRunStatusString2],
                excelData[props.MotorChannelSettingRunStatusString3],
                excelData[props.MotorChannelSettingRunStatusString4],
                excelData[props.MotorChannelSettingRunStatusString5],
                excelData[props.MotorChannelSettingAlarmGroupNo],
                excelData[props.MotorChannelSettingReposeGroup1No],
                excelData[props.MotorChannelSettingReposeGroup2No],
                excelData[props.MotorChannelSettingDelayTimer],
                excelData[props.MotorChannelSettingAlarmLevel]
            ),
            systemAlarmSetting: this.toSystemAlarmSetting(
                channelType === ChannelType.SYSTEM,
                excelData[props.SystemAlarmSettingStatusString1],
                excelData[props.SystemAlarmSettingStatusString2],
                excelData[props.SystemAlarmSettingStatusString3],
                excelData[props.SystemAlarmSettingStatusString4],
                excelData[props.SystemAlarmSettingStatusString5],
                excelData[props.SystemAlarmSettingStatusString6],
                excelData[props.SystemAlarmSettingStatusString7],
                excelData[props.SystemAlarmSettingStatusString8],
                excelData[props.SystemAlarmSettingStatusString9],
                excelData[props.SystemAlarmSettingStatusString10],
                excelData[props.SystemAlarmSettingStatusString11],
                excelData[props.SystemAlarmSettingStatusString12],
                excelData[props.SystemAlarmSettingStatusString13],
                excelData[props.SystemAlarmSettingStatusString14],
                excelData[props.SystemAlarmSettingStatusString15],
                excelData[props.SystemAlarmSettingStatusString16],
                excelData[props.SystemAlarmSettingAlarmGroupNo],
                excelData[props.SystemAlarmSettingReposeGroup1No],
                excelData[props.SystemAlarmSettingReposeGroup2No],
                excelData[props.SystemAlarmSettingDelayTimer],
                excelData[props.SystemAlarmSettingAlarmLevel]
            ),
            alarmSettingVeryHigh: this.toAlarmSettingExtra(
                isVHEnabled,
                excelData[props.AlarmSettingVeryHighStatusString] || 'HH',
                isVHEnabled && isValidValue(excelData[props.AlarmSettingVeryHighSetPoint])
                    ? Number(excelData[props.AlarmSettingVeryHighSetPoint]) * scale
                    : null,
                excelData[props.AlarmSettingVeryHighAlarmGroupNo],
                excelData[props.AlarmSettingVeryHighReposeGroup1No],
                excelData[props.AlarmSettingVeryHighReposeGroup2No],
                false,
                excelData[props.AlarmSettingVeryHighDelayTimer],
                excelData[props.AlarmSettingVeryHighAlarmLevel]
            ),
            alarmSettingHigh: this.toAlarmSettingExtra(
                isHEnabled,
                excelData[props.AlarmSettingHighStatusString] || 'HIGH',
                isHEnabled ? this.parseOptionalNumber(excelData[props.AlarmSettingHighSetPoint]) * scale : null,
                excelData[props.AlarmSettingHighAlarmGroupNo],
                excelData[props.AlarmSettingHighReposeGroup1No],
                excelData[props.AlarmSettingHighReposeGroup2No],
                false,
                excelData[props.AlarmSettingHighDelayTimer],
                excelData[props.AlarmSettingHighAlarmLevel]
            ),
            alarmSettingLow: this.toAlarmSettingExtra(
                isLEnabled,
                excelData[props.AlarmSettingLowStatusString] || 'LOW',
                isLEnabled ? this.parseOptionalNumber(excelData[props.AlarmSettingLowSetPoint]) * scale : null,
                excelData[props.AlarmSettingLowAlarmGroupNo],
                excelData[props.AlarmSettingLowReposeGroup1No],
                excelData[props.AlarmSettingLowReposeGroup2No],
                false,
                excelData[props.AlarmSettingLowDelayTimer],
                excelData[props.AlarmSettingLowAlarmLevel]
            ),
            alarmSettingVeryLow: this.toAlarmSettingExtra(
                isVLEnabled,
                excelData[props.AlarmSettingVeryLowStatusString] || 'LL',
                isVLEnabled ? this.parseOptionalNumber(excelData[props.AlarmSettingVeryLowSetPoint]) * scale : null,
                excelData[props.AlarmSettingVeryLowAlarmGroupNo],
                excelData[props.AlarmSettingVeryLowReposeGroup1No],
                excelData[props.AlarmSettingVeryLowReposeGroup2No],
                false,
                excelData[props.AlarmSettingVeryLowDelayTimer],
                excelData[props.AlarmSettingVeryLowAlarmLevel]
            ),
            alarmSettingSensorFail: this.toAlarmSettingExtra(
                excelData[props.AlarmSettingSensorFailEnable],
                excelData[props.AlarmSettingSensorFailStatusString] ?? 'FAIL',
                null,
                excelData[props.AlarmSettingSensorFailAlarmGroupNo],
                excelData[props.AlarmSettingSensorFailReposeGroup1No],
                excelData[props.AlarmSettingSensorFailReposeGroup2No],
                false,
                excelData[props.AlarmSettingSensorFailDelayTimer],
                excelData[props.AlarmSettingSensorFailAlarmLevel]
            ),
            alarmSettingFeedbackError: this.toAlarmSettingExtra(
                excelData[props.AlarmSettingFeedbackErrorEnable],
                excelData[props.AlarmSettingFeedbackErrorStatusString] ?? 'FEEDBACK',
                null,
                excelData[props.AlarmSettingFeedbackErrorAlarmGroupNo],
                excelData[props.AlarmSettingFeedbackErrorReposeGroup1No],
                excelData[props.AlarmSettingFeedbackErrorReposeGroup2No],
                false,
                excelData[props.AlarmSettingFeedbackErrorDelayTimer],
                excelData[props.AlarmSettingFeedbackErrorAlarmLevel]
            ),
            standardNameId: excelData[props.StandardNameId]?.toString() ?? '',
        };

        const statusString = excelData[props.Status]?.toString();
        if (statusString) {
            channelConfig = setChannelStatusFromString(channelConfig, statusString);
        }

        if (channelConfig.channelType === undefined) {
            throw new ExcelImportError(ErrorCodes.VALUE_NOT_ALLOWED, {
                value: excelData[props.ChannelType],
                columnHeader: props.ChannelType,
            });
        }

        if (channelConfig.channelType === systemChannelType) {
            channelConfig.fcuNo = null;
            channelConfig.measurableRange = null;
            channelConfig.normalRange = null;
            channelConfig.scale = null;
            channelConfig.fixedDigit = null;
            channelConfig.inputData = null;
            channelConfig.outputData = null;
            channelConfig.digitalAlarmSetting = null;
            channelConfig.motorChannelSetting = null;
            channelConfig.alarmSettingVeryHigh = null;
            channelConfig.alarmSettingHigh = null;
            channelConfig.alarmSettingLow = null;
            channelConfig.alarmSettingVeryLow = null;
            channelConfig.alarmSettingSensorFail = null;
            channelConfig.alarmSettingFeedbackError = null;
            channelConfig.standardNameId = null;
        }

        return channelConfig;
    }

    static parseCategory(excelData: any, sheetLength) {
        const parsedCategories = [];
        const parsedGroups = [];
        const tempCategoriesMap = new Map();
        const startRow = 4;

        for (let i = startRow; i < excelData.length; i++) {
            let row = excelData[i];
            if (row[GroupsSheetColumns.categoryExcelId] !== '' && row[GroupsSheetColumns.categoryName] !== '') {
                let category = {
                    excelId: row[GroupsSheetColumns.categoryExcelId],
                    category: row[GroupsSheetColumns.categoryName],
                    rowId: row[GroupsSheetColumns.rowId],
                };
                parsedCategories.push(category);
                tempCategoriesMap.set(row[GroupsSheetColumns.rowId], category);
            }
            if (row[GroupsSheetColumns.groupName] !== '') {
                let group = {
                    rowNum: row[GroupsSheetColumns.rowNum],
                    excelId: row[GroupsSheetColumns.groupExcelId],
                    displayName: row[GroupsSheetColumns.groupName],
                    categoryRowId: row[GroupsSheetColumns.categoryRowId],
                };
                parsedGroups.push(group);
            }
        }

        parsedGroups.forEach(group => {
            const category = tempCategoriesMap.get(group.categoryRowId);
            group.categoryExcelId = category?.excelId;
        });

        return { parsedCategories, parsedGroups };
    }

    private static parseHeaders(spreadsheet: any) {
        const headers = Array(spreadsheet[1]?.length).fill('');

        for (let rowI = 1; rowI < ExcelHeaderRowCount; rowI++) {
            const headerRow = spreadsheet[rowI];
            const previousRow = spreadsheet[rowI - 1];

            for (let columnI = 0; columnI < headerRow.length; columnI++) {
                if (columnI != 0 && !headerRow[columnI] && headers[columnI - 1].startsWith(headers[columnI])) {
                    headerRow[columnI] = headerRow[columnI - 1];
                }

                if (headerRow[columnI]) {
                    const parsedHeader = headerRow[columnI]
                        .replace(/[\r\n]+/g, ' ')
                        .trim()
                        .toLowerCase();

                    headers[columnI] = headers[columnI] ? headers[columnI] + '.' + parsedHeader : parsedHeader;
                }
            }
        }

        return headers.map(normalizeLegacyExcelHeader);
    }

    private static getScale(num: number | string): number {
        if (num == null) {
            return 1;
        }

        const scaleStr = String(num);
        const decimals = scaleStr.split('.')[1]?.length ?? 0;
        return decimals === 0 ? 1 : 10 ** decimals;
    }

    private static isBlankExceptionChannelNo(columnData: any, columnName: string) {
        return columnData !== '' && ExcelAllowedHeaders[ExcelParsedProperties.Tag] !== columnName;
    }

    private static validIdIsUnique(parsedData: any, columnName: any) {
        const seenCell = new Set();

        for (let i = 0; i < parsedData.length; i++) {
            if (!ExcelSkipParsingSheets.has(parsedData[i].sheetName)) {
                for (let j = 0; j < parsedData[i].data.length; j++) {
                    if (parsedData[i].data[j]) {
                        if (
                            seenCell.has(parsedData[i].data[j][columnName]) &&
                            this.isBlankExceptionChannelNo(parsedData[i].data[j][columnName], columnName)
                        ) {
                            let duplicates = {
                                column: columnName,
                                row: j + ExcelHeaderRowCount + 1,
                                sheetName: parsedData[i].sheetName,
                            };
                            return duplicates;
                        }
                        seenCell.add(parsedData[i].data[j][columnName]);
                    }
                }
            }
        }

        return undefined;
    }

    private static parseIntData(parsedData: string | number) {
        if (typeof parsedData == 'string' && !parsedData.includes('-')) {
            return parseInt(parsedData, 10);
        }
        return parsedData;
    }

    private static toAlarmSetting(
        enable: any,
        contactOn: any,
        contactOff: any,
        groupNo: any,
        reposeGroup1: any,
        resposeGroup2: any,
        delay: any,
        normalOpen: any,
        alarmLevel: any,
        manualRepose: any
    ) {
        return {
            enable: enable === 'TRUE' || enable === true,
            statusStringContactOn: contactOn,
            statusStringContactOff: contactOff,
            alarmGroupNo: this.parseOptionalNumber(groupNo),
            reposeGroup1No: this.parseOptionalNumber(reposeGroup1),
            reposeGroup2No: this.parseOptionalNumber(resposeGroup2),
            delayTimer: this.parseOptionalNumber(delay),
            normalOpenFlag: normalOpen == 'o' || normalOpen == true,
            alarmLevel: this.alarmValueOrDefault(alarmLevel),
            manualReposeFlag: manualRepose && (manualRepose == 'o' || manualRepose == true),
        };
    }

    private static toAlarmSettingMotor(
        enable: any,
        motorType: any,
        stopStatus: any,
        stbyStatus: any,
        runStatus1: any,
        runStatus2: any,
        runStatus3: any,
        runStatus4: any,
        runStatus5: any,
        groupNo: any,
        reposeGroup1: any,
        reposeGroup2: any,
        delay: any,
        alarmLevel: any
    ) {
        return {
            enable: enable === 'TRUE' || enable === true,
            motorType: String(motorType || (enable ? MotorType.RUN : '')),
            stopStatusString: String(stopStatus || (enable ? 'STOP' : '')),
            stbyStatusString: String(stbyStatus || ''),
            runStatusString1: String(runStatus1 || (enable ? 'RUN' : '')),
            runStatusString2: String(runStatus2 || ''),
            runStatusString3: String(runStatus3 || ''),
            runStatusString4: String(runStatus4 || ''),
            runStatusString5: String(runStatus5 || ''),
            alarmGroupNo: this.parseOptionalNumber(groupNo),
            reposeGroup1No: this.parseOptionalNumber(reposeGroup1),
            reposeGroup2No: this.parseOptionalNumber(reposeGroup2),
            delayTimer: this.parseOptionalNumber(delay),
            alarmLevel: this.alarmValueOrDefault(alarmLevel),
        };
    }

    private static toSystemAlarmSetting(
        enable: any,
        statusString1: any,
        statusString2: any,
        statusString3: any,
        statusString4: any,
        statusString5: any,
        statusString6: any,
        statusString7: any,
        statusString8: any,
        statusString9: any,
        statusString10: any,
        statusString11: any,
        statusString12: any,
        statusString13: any,
        statusString14: any,
        statusString15: any,
        statusString16: any,
        groupNo: any,
        reposeGroup1: any,
        reposeGroup2: any,
        delay: any,
        alarmLevel: any
    ) {
        return {
            enable: enable === 'TRUE' || enable === true,
            statusString1: String(statusString1 || ''),
            statusString2: String(statusString2 || ''),
            statusString3: String(statusString3 || ''),
            statusString4: String(statusString4 || ''),
            statusString5: String(statusString5 || ''),
            statusString6: String(statusString6 || ''),
            statusString7: String(statusString7 || ''),
            statusString8: String(statusString8 || ''),
            statusString9: String(statusString9 || ''),
            statusString10: String(statusString10 || ''),
            statusString11: String(statusString11 || ''),
            statusString12: String(statusString12 || ''),
            statusString13: String(statusString13 || ''),
            statusString14: String(statusString14 || ''),
            statusString15: String(statusString15 || ''),
            statusString16: String(statusString16 || ''),
            alarmGroupNo: this.parseOptionalNumber(groupNo),
            reposeGroup1No: this.parseOptionalNumber(reposeGroup1),
            reposeGroup2No: this.parseOptionalNumber(reposeGroup2),
            delayTimer: this.parseOptionalNumber(delay),
            alarmLevel: this.alarmValueOrDefault(alarmLevel),
        };
    }

    private static toAlarmSettingExtra(
        enable: any,
        status: any,
        setPoint: any,
        groupNo: any,
        reposeGroup1: any,
        resposeGroup2: any,
        manualRepose: any,
        delay: any,
        alarmLevel: any
    ) {
        const isEnabled = enable === 'TRUE' || enable === true;

        return {
            enable: isEnabled,
            statusString: status,
            setPoint: isEnabled ? this.parseOptionalNumber(setPoint) : null,
            alarmGroupNo: this.parseOptionalNumber(groupNo),
            reposeGroup1No: this.parseOptionalNumber(reposeGroup1),
            reposeGroup2No: this.parseOptionalNumber(resposeGroup2),
            manualReposeFlag: manualRepose && (manualRepose == 'o' || manualRepose == true),
            delayTimer: this.parseOptionalNumber(delay),
            alarmLevel: this.alarmValueOrDefault(alarmLevel),
        };
    }

    private static alarmValueOrDefault(value: any) {
        if (value == '' || value == null) {
            return AlarmLevel.None;
        } else {
            let parsedValue = Number(value);

            if (Number.isNaN(parsedValue)) {
                parsedValue = ExcelAlarmStringValues[value.trim().toLowerCase()];
            }

            return parsedValue;
        }
    }

    private static toRange(max: any, min: any, scale: number = 1) {
        return {
            max: Math.round(parseFloat(max?.toString()) * scale),
            min: Math.round(parseFloat(min?.toString()) * scale),
        };
    }

    private static parseInputData(excelDatas: any) {
        return {
            fuInputAddress: {
                fuNo: this.parseOptionalNumber(excelDatas[props.InputDataFuInputAddressFuNo]),
                slotNo: this.parseOptionalNumber(excelDatas[props.InputDataFuInputAddressSlotNo]),
                pin: this.parseOptionalNumber(excelDatas[props.InputDataFuInputAddressPin]),
                pinCount: this.parseOptionalNumber(excelDatas[props.InputDataFuInputAddressPinCount]),
            },
            memoryInputAddress: {
                address: this.parseOptionalNumber(excelDatas[props.InputDataMemoryInputAddressAddress]),
                mask: this.parseOptionalNumber(excelDatas[props.InputDataMemoryInputAddressMask]),
            },
            misc: {
                digitalInputFilter: this.parseOptionalNumber(excelDatas[props.InputDataMiscDigitalInputFilter]) ?? '',
                hysteresis: this.parseOptionalNumber(excelDatas[props.InputDataMiscHysteresis]) ?? '',
            },
        };
    }

    private static parseOutputData(excelDatas: any) {
        const setPoint = this.parseOptionalNumber(excelDatas[props.OutputDataMiscSetPoint]);

        return {
            fuOutputAddress: {
                fuNo: this.parseOptionalNumber(excelDatas[props.OutputDataFuOutputAddressFuNo]),
                slotNo: this.parseOptionalNumber(excelDatas[props.OutputDataFuOutputAddressSlotNo]),
                pin: this.parseOptionalNumber(excelDatas[props.OutputDataFuOutputAddressPin]),
                pinCount: this.parseOptionalNumber(excelDatas[props.OutputDataFuOutputAddressPinCount]),
            },
            memoryOutputAddress: {
                address: this.parseOptionalNumber(excelDatas[props.OutputDataMemoryOutputAddressAddress]),
                mask: this.parseOptionalNumber(excelDatas[props.OutputDataMemoryOutputAddressMask]),
            },
            misc: {
                setPoint: isFinite(setPoint) ? parseInt(setPoint?.toString().replace('.', ''), 10) : 0,
                signalType: excelDatas[props.OutputDataMiscSignalType],
                signalWidth: this.parseOptionalNumber(excelDatas[props.OutputDataMiscSignalWidth]) ?? '',
                hysteresisForOpen: this.parseOptionalNumber(excelDatas[props.OutputDataMiscHysteresisForOpen]),
                hysteresisForClose: this.parseOptionalNumber(excelDatas[props.OutputDataMiscHysteresisForClose]),
            },
        };
    }
}
